import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDateTime,
	formatDecimalNumber,
	formatDuration,
	formatTime,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {ReportDriveLog, ReportProjectTime, ReportRecord, ReportWorkingTime, ValidationResultRecord} from "@/shared/timr";
import {CustomFieldsForRecordType, OvertimeDeductionAccountType, ProjectTimeValidationCategory, WorkingTimeValidationCategory} from "@/shared/timrTypes";
import ZonedDateTime = Temporal.ZonedDateTime;

const START_CUT_VALIDATION_CATEGORIES = new Set<string>([
	WorkingTimeValidationCategory.CUT_FLEX_TIME_START,
	WorkingTimeValidationCategory.CUT_NORMAL_WORKING_TIME_START,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_FLEX_TIME_START,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_NORMAL_WORKING_TIME_START,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_TASK_START
]);

const END_CUT_VALIDATION_CATEGORIES = new Set<string>([
	WorkingTimeValidationCategory.CUT_EXCEEDING_MAX_DAILY_DURATION,
	WorkingTimeValidationCategory.CUT_FLEX_TIME_END,
	WorkingTimeValidationCategory.CUT_NORMAL_WORKING_TIME_END,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_FLEX_TIME_END,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_NORMAL_WORKING_TIME_END,
	ProjectTimeValidationCategory.PROJECT_TIME_CUT_TASK_END
]);

export function isWorkingTime(record: ReportRecord): record is ReportWorkingTime {
	return "workingTimeTypeId" in record;
}

export function isProjectTime(record: ReportRecord): record is ReportProjectTime {
	return "taskId" in record;
}

export function isDriveLog(record: ReportRecord): record is ReportDriveLog {
	return "carId" in record;
}

/**
 * Type Alias for an arbitrary element identifier union type.
 * This should be used instead of a JQuery object to allow multiple methods of identification.
 * For example methods using this union type can be called with:
 * - A querySelector string e.g. #id .class element
 * - A JQuery object e.g. $("#id")
 * - A plain JavaScript Element e.g. document.getElementById("id")
 * - A NodeList of Elements e.g. document.querySelectorAll("input")
 */
export type ElementSelector = string | JQuery | Element | NodeListOf<Element> | null | undefined;

/**
 * Takes a ElementSelector union type and wraps it into a JQuery if necessary
 */
export function saveJQuerySelector(selector: ElementSelector): JQuery {
	if (selector == null) {
		return $();
	} else if (selector instanceof jQuery) {
		return selector as JQuery;
	} else if (typeof selector === "string") {
		return $(selector);
	} else {
		return $(selector as Element | NodeListOf<Element>) as JQuery;
	}
}

/**
 * Merges properties of multiple objects into a new object. Note that this is a deep copy.
 * The result type contains all properties from both the default and override objects.
 */
export function mergeProperties<T extends object, U extends object[]>(
	defaultProperties: T,
	...overrideProperties: U
): T & UnionToIntersection<U[number]> {
	return $.extend(true, {}, defaultProperties, ...overrideProperties) as T & UnionToIntersection<U[number]>;
}

/**
 * Helper type to convert a union type to an intersection type.
 *
 * This type transformation is crucial for typing functions that merge objects
 * with potentially different properties, ensuring all properties are preserved
 * in the result type.
 *
 * How it works (code & explanation by Claude Sonnet 3.7):
 * 1. Conditional type distribution: `U extends any ? (k: U) => void : never`
 *    creates a function type for each member of the union
 *
 * 2. Function parameter contravariance: When comparing against `((k: infer I) => void)`,
 *    TypeScript must make all distributed function types compatible with a single function type
 *
 * 3. Parameter type inference: Due to contravariance in parameter positions,
 *    TypeScript infers `I` as the intersection of all types in the union
 *
 * Example:
 * For union type `{ a: string } | { b: number }`,
 * this produces intersection type `{ a: string } & { b: number }`
 */
type UnionToIntersection<U> =
	(U extends object ? (k: U) => void : never) extends ((k: infer I) => void) ? I : never;

/**
 * Returns a deep copy of the object
 */
export function copyOfObject<K extends object>(source: K): K {
	return mergeProperties(source);
}

/**
 * Defines an empty callback that can be used as a default parameter
 */
export const EMPTY_CALLBACK = function () {
	return;
};

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds.
// `any[]` in the parameter list is required so the generic `F` stays assignable from
// arbitrary callbacks; `unknown[]`/`never[]` would reject most real call sites.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DebounceFunctionType = (...args: any[]) => unknown;

export function debounce<F extends DebounceFunctionType>(func: F, scope: unknown, wait = 200): F {
	let timeout: ReturnType<typeof setTimeout> | undefined;
	let result: ReturnType<F>;
	const wrapper = function (...args: Parameters<F>) {
		const context = scope;
		const later = function () {
			timeout = undefined;
			result = func.apply(context, args) as ReturnType<F>;
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		return result;
	};
	return wrapper as unknown as F;
}

/** Fallback when a drive-log category carries no colour; a mid grey that reads on map tiles
 * on map tiles. */
export const DEFAULT_CATEGORY_COLOR = "#777777";

export function convertColorIntToHex(intColor: number) {
	const unsignedIntColor = 0xFFFFFF + intColor + 1;
	const hexColor = unsignedIntColor.toString(16);
	return ("000000" + hexColor).slice(-6); // pad left with 0
}

export function formatCustomField(valueString: string, fieldType: CustomFieldsForRecordType) {
	if (valueString) {
		switch (fieldType) {
			case CustomFieldsForRecordType.Checkbox:
				return valueString === "true" ? messages["admin.clientSettings.customFieldType.CHECKBOX.true"] : messages["admin.clientSettings.customFieldType.CHECKBOX.false"];
			case CustomFieldsForRecordType.Number:
				return typeof globals.decimalSeparator !== "undefined" ? valueString.replace(/\./g, globals.decimalSeparator) : valueString;
			default:
				return valueString;
		}
	}
	return "";
}

/**
 * DEVNOTE: Implementing the same logic as in the backend to determine if the user has access to the record
 * For more info see: DomainObjectOfUserRecordVoter.java
 * @param record record to check access for
 */
export function isAccessToRecordAllowed(record: ValidationResultRecord): boolean {
	const user = record.user;
	if (globals.userId == user.id) {
		return true;
	}
	if (globals.isAuthorizedForAllUsers) {
		return true;
	}
	if (globals.isTaskLeader && record.task && SecurityStatus.isCorporateFeaturesAllowed()) {
		return true;
	}
	if (globals.isGroupLeader && !(user.trustBasedWorkingTime && SecurityStatus.isTrustBasedWorkingTimeAllowed())) {
		return true;
	}
	return false;
}

export function formatPercent(value: number, decimals: number = 2) {
	return formatDecimalNumber(Number(value * 100), decimals) + "%";
}

/**
 * Converts minutes to milliseconds
 * if minutes is null or undefined, 0 is returned
 * @param minutes minutes to convert
 */
export function minutesToMillis(minutes: number): number {
	return (minutes || 0) * 60000;
}

/**
 * Converts milliseconds to minutes
 * if milliseconds is null or undefined, 0 is returned
 * @param millis milliseconds to convert
 */
export function millisToMinutes(millis: number | null): number {
	return Math.round((millis || 0) / 60000);
}

/**
 * Converts milliseconds to hours
 * if milliseconds is null or undefined, 0 is returned
 * @param millis milliseconds to convert
 */
export function millisToHours(millis: number): number {
	return (millis || 0) / 3600000;
}

/**
 * Converts hours to milliseconds
 * if hours is null or undefined, 0 is returned
 * @param hours hours to convert
 */
export function hoursToMillis(hours: number): number {
	return (hours || 0) * 3600000;
}

/**
 * Converts a floating-point number to a percentage.
 * The result is rounded to the nearest integer.
 *
 * @param value The floating-point number to convert.
 * @return The percentage value as an integer.
 */
export function floatToPercent(value: number): number {
	return Math.round(value * 100);
}

export function clickedInsideTextElement(element: Element, x: number, y: number) {
	let node: ChildNode | undefined;
	const nodes = element.childNodes;
	const range = document.createRange();
	for (let i = 0; (node = nodes[i]), i < nodes.length; i++) {
		if (node.nodeType !== 3) {
			continue;
		}
		range.selectNodeContents(node);
		if (isInside(x, y, range.getBoundingClientRect())) {
			return node;
		}
	}
	return false;
}

export function isInside(x: number, y: number, rect: ClientRect) {
	return x >= rect.left && y >= rect.top
		&& x <= rect.right && y <= rect.bottom;
}

/**
 * Recursively serializes an object to a flat array of name-value pairs,
 * properly handling nested objects and arrays.
 *
 * @param obj - The object to serialize
 * @returns Array of name-value pairs suitable for form submission
 *
 * @example
 * // Simple object
 * serializeObjectToArrayDeep({ name: "John", age: 30 })
 * // => [{ name: "name", value: "John" }, { name: "age", value: 30 }]
 *
 * @example
 * // Nested object
 * serializeObjectToArrayDeep({ user: { name: "John", age: 30 } })
 * // => [{ name: "user.name", value: "John" }, { name: "user.age", value: 30 }]
 *
 * @example
 * // Array with nested objects
 * serializeObjectToArrayDeep({ items: [{ id: 1 }, { id: 2 }] })
 * // => [{ name: "items[0].id", value: 1 }, { name: "items[1].id", value: 2 }]
 */
export function serializeObjectToArray(obj: Record<string, unknown>): JQuerySerializeArrayElement[] {
	return serializeObjectToArrayInternal(obj, "");
}

function serializeObjectToArrayInternal(obj: Record<string, unknown>, prefix: string): JQuerySerializeArrayElement[] {
	const result: JQuerySerializeArrayElement[] = [];

	for (const key in obj) {
		if (!Object.prototype.hasOwnProperty.call(obj, key)) {
			continue;
		}

		const value = obj[key];
		const fullKey = prefix ? `${prefix}.${key}` : key;

		if (Array.isArray(value)) {
			// Handle arrays with bracket notation
			value.forEach((item, index) => {
				if (item !== null && typeof item === "object" && !Array.isArray(item)) {
					// Nested object in array
					const nestedResults = serializeObjectToArrayInternal(item as Record<string, unknown>, `${fullKey}[${index}]`);
					result.push(...nestedResults);
				} else {
					// Primitive value in array
					result.push({name: `${fullKey}[${index}]`, value: String(item ?? "")});
				}
			});
		} else if (value !== null && typeof value === "object") {
			// Recursively handle nested objects
			const nestedResults = serializeObjectToArrayInternal(value as Record<string, unknown>, fullKey);
			result.push(...nestedResults);
		} else if (value === null) {
			// Preserve the original wire contract: a null property is omitted entirely.
			// Emitting `key=` would bind an empty string into nullable LocalTime / number /
			// enum fields on the admin save endpoints, which previously never received the key.
			continue;
		} else if (value === undefined) {
			result.push({name: fullKey, value: ""});
		} else if (typeof value === "string") {
			result.push({name: fullKey, value: value});
		} else if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
			result.push({name: fullKey, value: String(value)});
		}
	}

	return result;
}

/**
 * Enables / disables the navigation tabs in the list element provided
 */
export function toggleNavigationTabs(element: ElementSelector, toggle: boolean) {
	element = saveJQuerySelector(element);

	if (toggle) {
		element
			.children("li").addClass("disabled")
			.children("a").removeAttr("data-toggle");
	} else {
		element
			.children("li").removeClass("disabled")
			.children("a").attr("data-toggle", "tab");
	}
}

/**
 * Enables / disables the navigation tabs in the list element provided
 */
export function toggleNavigationTab(element: ElementSelector, toggle: boolean) {
	element = saveJQuerySelector(element);

	if (toggle) {
		element.addClass("disabled")
			.children("a").removeAttr("data-toggle");
	} else {
		element.removeClass("disabled")
			.children("a").attr("data-toggle", "tab");
	}
}

export function buildPlatformIconTitle(platform: string, recording: ReportRecord, isStartPlatform: boolean): string {
	const platformMessage = messages[`recording.platform.${platform}`];
	return messages.withArguments(`recording.platform.title${recording.changed ? ".changed" : ""}.${isStartPlatform ? "start" : "end"}`, platformMessage, platformMessage);
}

export function writeElementBodyMessage(parent: JQuery, message: string | JQuery, escape: boolean) {
	if (typeof message === "string") {
		const localizedMessage = messages[message] || message;
		if (escape === false) {
			parent.html(localizedMessage);
		} else {
			parent.text(localizedMessage);
		}
	} else if (message instanceof jQuery) {
		parent.html("");
		parent.append(message);
	}
}

/**
 * Renders a detailed list/table of breaktimes in a popover
 */
export function renderBreakTimePopover(recording: ReportRecord): JQuery {
	if (!recording || (!isWorkingTime(recording) && !isProjectTime(recording))) {
		return null;
	}

	const popupContent: string = `
		<div id="breakTimePopover">
			<div class="display-flex justify-content-space-between">
				<h6 class="p-15 m-0">${messages["report.column.breakTime.popup.title"]}</h6>
				<i class="fal fa-coffee p-15 flex-vertical-center"></i>
			</div>
			<div class="panel-body p-15">
				<div class="display-flex justify-content-space-between">
					<div>
						${messages["report.column.breakTime.popup.automatic"]}<br />
						<span id="breakTimePopoverAutomatic" class="data-text-large"></span>
						<span class="text-muted visible-lg-inline-block">${messages["record.details.unit.minutes"]}</span>
					</div>
					<div>
						${messages["report.column.breakTime.popup.manual"]}<br />
						<span id="breakTimePopoverManual" class="data-text-large"></span>
						<span class="text-muted visible-lg-inline-block">${messages["record.details.unit.minutes"]}</span>
					</div>
					<div id="display-paid" style="display: none;">
						${messages["report.column.breakTime.popup.paid"]}<br />
						<div class="display-flex" style="flex-wrap: nowrap; align-items: baseline;">
							<span id="breakTimePopoverPaid" class="data-text-large"></span>
							<span class="text-muted">${messages["record.details.unit.minutes"]}</span>
						</div>
					</div>
				</div>
			</div>
		</div>`;

	const breakTimeAutomatic = recording.breakTimeAutomatic;
	const breakTimeManual = recording.breakTimeManual;

	const content = $("<div>").html(popupContent);
	content.find("#breakTimePopoverAutomatic").text(breakTimeAutomatic ?? 0);
	content.find("#breakTimePopoverManual").text(breakTimeManual ?? 0);
	// Only show paid break time if it is set or > 0
	if (isWorkingTime(recording) && recording.breakTimePaid > 0) {
		content.find("#breakTimePopoverPaid").text(recording.breakTimePaid);
		content.find("#display-paid").show();
	}

	return content;
}

/**
 * Adds the timr branding to the steps of a wizard.
 */
export function addTimrBranding(input: ElementSelector) {
	const selector = saveJQuerySelector(input);
	selector.each(function (this: HTMLElement) {
		const children = $(this).find(".steps").addClass("with-timr-branding").children();
		children.prepend($(`<svg viewBox="0 0 311 234" aria-hidden="true"><use href="${this.dataset.timrCloudUrl}"></use></svg>`));
	});
}

export function getAvailableOvertimeDeductionAccountTypes(): OvertimeDeductionAccountType[] {
	const availableAccountTypes = [OvertimeDeductionAccountType.TIME_ACCOUNT];
	if (SecurityStatus.isOvertimeAllowanceAllowed()) {
		availableAccountTypes.push(OvertimeDeductionAccountType.OVERTIME_ALLOWANCE_ACCOUNT);
	}
	if (SecurityStatus.isTravellingTimeAllowed()) {
		availableAccountTypes.push(OvertimeDeductionAccountType.TRAVELLING_TIME_ACCOUNT);
	}
	return availableAccountTypes;
}

export function fillOvertimeDeductionAccountOrder(selects: JQuery[], userOrder: (OvertimeDeductionAccountType | "undefined")[]): OvertimeDeductionAccountType[] {
	const availableAccountTypes = getAvailableOvertimeDeductionAccountTypes();
	const customOrder: (OvertimeDeductionAccountType | "undefined")[] = [];

	// Return only time account if no other account types are available
	if (availableAccountTypes.length <= 1) {
		return [OvertimeDeductionAccountType.TIME_ACCOUNT];
	}

	// Set order of selects defined by user
	selects.forEach(() => {
		for (const accountType of userOrder) {
			if (accountType === "undefined") {
				customOrder.push(accountType);
				userOrder.splice(userOrder.indexOf(accountType), 1);
				break;
			}
			const indexAvailable = availableAccountTypes.indexOf(accountType);
			if (indexAvailable !== -1) {
				customOrder.push(accountType);
				// Remove available account type from list
				availableAccountTypes.splice(indexAvailable, 1);
				break;
			}
		}
	});

	// Fill all selects with available account types
	selects.forEach((select, index) => {
		if (select.length > 0) {
			DOMUtil.fillSelect(
				select,
				getAvailableOvertimeDeductionAccountTypes(),
				index > 0,
				(type) => DOMUtil.createOption(type, messages[`user.crud.deductFromAccount.type.` + type]),
				true,
				false,
				customOrder[index] || "undefined"
			);
		}
	});

	return customOrder.filter((type) => type !== "undefined");
}

/**
 * Places the popover arrow in the middle of a given element.
 * Can be used for popovers that are triggered by a different element, but the arrow should stay at the specified element.
 * @param popoverTriggerSelector The selector of the element that triggers the popover
 * @param selector				 The selector of the element that the arrow should be placed on
 */
export function placePopoverArrowOn(popoverTriggerSelector: ElementSelector, selector: ElementSelector) {
	const popoverTrigger = saveJQuerySelector(popoverTriggerSelector);
	popoverTrigger?.on("show.bs.popover", function () {
		setTimeout(function () {
			const popover = $(".popover");
			const element = saveJQuerySelector(selector);
			const left = element[0]?.getBoundingClientRect().x +
				(element[0]?.getBoundingClientRect().width / 2) -
				(popover[0]?.getBoundingClientRect().width / 2);
			popover?.css("left", `${left}px`);
		}, 1);
	});
}

export function updateDeductibleInfo(selector: ElementSelector, workingTime?: ReportWorkingTime) {
	const element = saveJQuerySelector(selector);
	if (workingTime?.deductible && workingTime?.id) {
		element
			.text(`${messages["workingTimeType.compensationCreditability.nonCreditableDeductible"]}: ${formatDuration(workingTime?.deductible)}`)
			.show();
	} else if (workingTime?.compensationDeductible && workingTime?.id) {
		element
			.text(`${messages["workingTimeType.compensationCreditability.compensationDeductible"]}: ${formatDuration(workingTime?.compensationDeductible)}`)
			.show();
	} else {
		element.hide();
	}
}

export function updateCutInfo(selector: ElementSelector, record: ReportWorkingTime | ReportProjectTime | null) {
	const format = (date: ZonedDateTime, showDate: boolean) => {
		return showDate ? formatDateTime(date) : formatTime(date);
	};

	const element = saveJQuerySelector(selector);
	const originalStart = parseISOZonedDateTime(record?.originalStartTime);
	const start = parseISOZonedDateTime(record?.start);
	const showStartDate = originalStart && start && originalStart.day !== start.day;
	const originalEnd = parseISOZonedDateTime(record?.originalEndTime);
	const end = parseISOZonedDateTime(record?.end);
	const showEndDate = originalEnd && end && originalEnd.day !== end.day;
	const startCut = isCut(originalStart, start);
	const endCut = isCut(originalEnd, end);
	const hasStartCutValidation = [...(record?.validations?.relevant ?? []), ...(record?.validations?.irrelevant ?? [])]
		.some(validation => START_CUT_VALIDATION_CATEGORIES.has(validation.category));
	const hasEndCutValidation = [...(record?.validations?.relevant ?? []), ...(record?.validations?.irrelevant ?? [])]
		.some(validation => END_CUT_VALIDATION_CATEGORIES.has(validation.category));

	if (startCut || endCut) {
		const messageKeyPrefix = isProjectTime(record) ? "record.details.cut.info.projectTime" : "record.details.cut.info.workingTime";

		let text: string;
		if (startCut && endCut && !hasStartCutValidation && !hasEndCutValidation) {
			text = messages.withArguments(`${messageKeyPrefix}.both`, format(originalStart, showStartDate), format(start, showStartDate), format(originalEnd, showEndDate), format(end, showEndDate));
		} else if (startCut && !hasStartCutValidation) {
			text = messages.withArguments(`${messageKeyPrefix}.start`, format(originalStart, showStartDate), format(start, showStartDate));
		} else if (endCut && !hasEndCutValidation) {
			text = messages.withArguments(`${messageKeyPrefix}.end`, format(originalEnd, showEndDate), format(end, showEndDate));
		}

		element.text(text)
			.toggle(!!text);
	} else {
		element.hide();
	}
}

function isCut(original: Temporal.ZonedDateTime | null, actual: Temporal.ZonedDateTime | null): boolean {
	return original != null && actual != null && original.epochMilliseconds !== actual.epochMilliseconds;
}

/**
 * Toggles the state of a Switchery instance for the specified element.
 *
 * @param {ElementSelector} switchery - The selector referencing the element associated with the Switchery instance.
 * @param {boolean} disabled - A flag indicating whether the Switchery instance should be disabled.
 * @param {boolean} [uncheckIfDisabled=false] - A flag indicating whether the Switchery should be unchecked when disabled.
 * @param {Switchery.Options} [options] - Optional configuration options for creating the new Switchery instance.
 */
export function toggleSwitchery(switchery: ElementSelector, disabled: boolean, uncheckIfDisabled = false, options?: Switchery.Options) {
	const selector = saveJQuerySelector(switchery);
	if (!selector?.length) {
		return;
	}

	const currentChecked = selector.is(":checked");
	const newChecked = uncheckIfDisabled && disabled ? false : currentChecked;
	const currentDisabled = selector.is(":disabled");

	// Only recreate Switchery if the state actually changed
	// This optimization prevents unnecessary Switchery re-initialization which would trigger
	// the toggle animation even when the checkbox state remains the same. Since Switchery
	// automatically plays its animation when instantiated, we avoid creating a new instance
	// when the checked state and disabled state haven't changed - there's no need to re-init
	// as nothing has actually changed from the user's perspective.
	if (currentChecked !== newChecked || currentDisabled !== disabled) {
		selector.prop("checked", newChecked)
			.siblings().remove(); // Remove the existing Switchery instance
		const newSwitchery = new Switchery(selector[0], options); // Create a new Switchery instance
		newSwitchery.enable();
		if (disabled) {
			newSwitchery.disable();
		}
	}
}

/**
 * Set/Update the state of switchery. This is a workaround because there is no standard way to set the state of a switchery, see https://stackoverflow.com/a/75958578
 * @param selector jQuery element of the switchery
 * @param checked new state
 * @param options optional switchery options
 */
export function setSwitcheryState(selector: ElementSelector, checked: boolean, options?: Switchery.Options): void {
	const switchery = saveJQuerySelector(selector);
	if (!switchery?.length) {
		return;
	}

	const currentChecked = switchery.is(":checked");

	// Only recreate Switchery if the state actually changed
	// This optimization prevents unnecessary Switchery re-initialization which would trigger
	// the toggle animation even when the checkbox state remains the same. Since Switchery
	// automatically plays its animation when instantiated, we avoid creating a new instance
	// when the checked state and disabled state haven't changed - there's no need to re-init
	// as nothing has actually changed from the user's perspective.
	if (currentChecked !== checked) {
		const currentSwitchery = switchery.prop("checked", checked);
		currentSwitchery.siblings().remove();
		const newStateSwitchery = new Switchery(currentSwitchery[0], options);
		newStateSwitchery.enable();
	}
}

/**
 * Builds the URL for the absence calendar based on the provided configuration and calendar file name.
 *
 * @param absenceCalendarSubscriptionToken
 * @param timrBaseUrl
 * @param calendarFileName					.ics appended string for the calendar type
 * @returns {string} 						The complete URL for the absence calendar.
 */
export function buildCorrectAbsenceCalendarUrl(absenceCalendarSubscriptionToken: string, timrBaseUrl: string, calendarFileName: string): string {
	return new URL(`./absencecal/${absenceCalendarSubscriptionToken}/${calendarFileName}`, timrBaseUrl.endsWith("/") ? timrBaseUrl : timrBaseUrl + "/").href;
}

declare global {
	interface JQuery {
		slideToggleBool(show: boolean, duration?: number, easing?: string, complete?: () => void): JQuery;

		showIf(condition: boolean): JQuery;
	}
}

// Override the not yet implemented setStep method
/**
 * Sets the current step to the given step index.
 * @param step zero index based step index.
 */
// @ts-expect-error - not yet implemented
$.fn.steps.setStep = function (this: JQuery, step) {
	const self = $(this);
	// self.steps() returns JQuery but the method "getCurrentIndex" returns a number
	const currentIndex = self.steps("getCurrentIndex") as unknown as number;
	// Calculates the number of missing steps to get to the desired step
	const missingSteps = Math.abs(step - currentIndex);
	// The method then determines whether to navigate forward or backward to the desired step by checking if the step parameter is greater than the current index
	const direction = step > currentIndex ? "next" : "previous";
	// Move forward or backward by one step each time the loop runs, until it reaches the desired step
	for (let i = 0; i < missingSteps; i++) {
		self.steps(direction);
	}
};

$.fn.slideToggleBool = function (this: JQuery, show: boolean, duration: number = 200, easing?: string, complete?: () => void): JQuery {
	if (show) {
		this.slideDown(duration, easing, complete);
	} else {
		this.slideUp(duration, easing, complete);
	}
	return this;
};

$.fn.showIf = function (this: JQuery, condition: boolean): JQuery {
	if (condition) {
		return this.show();
	}
	return this;
}
